import { gql, useLazyQuery } from '@apollo/client';
import { openWebBrowserURL } from '@/utils/navigation';
import {
  OrderGuide_ExportPriceQuoteDetailsCsvQuery,
  OrderGuide_ExportPriceQuoteDetailsCsvQueryVariables,
  OrderGuide_RestaurantAddressQueryQuery,
  OrderGuide_RestaurantAddressQueryQueryVariables,
} from '@/types';
import { useCallback } from 'react';

export const VIEW_PRICE_QUOTE_DETAILS_KEY = gql`
  query OrderGuide_RestaurantAddressQuery(
    $orderGuideCollectionUUID: uuid!
    $employeeUUID: uuid!
  ) {
    getOrderGuidePriceQuoteKey(
      order_guide_collection_id: $orderGuideCollectionUUID
      employee_id: $employeeUUID
    ) {
      public_url
    }
  }
`;

export const EXPORT_PRICE_QUOTE_QUERY = gql`
  query OrderGuide_ExportPriceQuoteDetailsCsv($priceQuoteKey: String!) {
    getPriceQuoteDetails(price_quote_key: $priceQuoteKey, file_format: CSV) {
      price_quote_document_url
      supplier_accent_color
      supplier_app_icon_image_url
      supplier_name
    }
  }
`;

export const useGetOpenPriceQuotePDF = () => {
  const [getPriceQuoteDetailsKey, { loading }] = useLazyQuery<
    OrderGuide_RestaurantAddressQueryQuery,
    OrderGuide_RestaurantAddressQueryQueryVariables
  >(VIEW_PRICE_QUOTE_DETAILS_KEY);

  const openPriceQuotePDF = useCallback(
    async (
      orderGuideRestaurantCollectionUUID: string,
      employeeUUID: string,
    ) => {
      const { data, error } = await getPriceQuoteDetailsKey({
        variables: {
          employeeUUID,
          orderGuideCollectionUUID: orderGuideRestaurantCollectionUUID,
        },
      });

      if (error || !data?.getOrderGuidePriceQuoteKey.public_url) {
        throw new Error('Not able to get Price Quote details key');
      }

      // Set source to prevent the app banner from rendering
      await openWebBrowserURL(
        `${data.getOrderGuidePriceQuoteKey.public_url}?source=app`,
      );
    },
    [getPriceQuoteDetailsKey],
  );

  return { loading, openPriceQuotePDF };
};

export const useGetPublicLinkPriceQuoteCSV = () => {
  const [getPriceQuoteDetailsKey, { loading }] = useLazyQuery<
    OrderGuide_RestaurantAddressQueryQuery,
    OrderGuide_RestaurantAddressQueryQueryVariables
  >(VIEW_PRICE_QUOTE_DETAILS_KEY);

  const [exportPriceQuoteFile, { loading: exportLoading }] = useLazyQuery<
    OrderGuide_ExportPriceQuoteDetailsCsvQuery,
    OrderGuide_ExportPriceQuoteDetailsCsvQueryVariables
  >(EXPORT_PRICE_QUOTE_QUERY);

  const exportPriceQuoteCSV = useCallback(
    async (
      orderGuideRestaurantCollectionUUID: string,
      employeeUUID: string,
    ) => {
      const { data, error } = await getPriceQuoteDetailsKey({
        variables: {
          employeeUUID,
          orderGuideCollectionUUID: orderGuideRestaurantCollectionUUID,
        },
      });

      if (error || !data?.getOrderGuidePriceQuoteKey.public_url) {
        throw new Error('Not able to get Price Quote details key');
      }

      const detailsKey = data.getOrderGuidePriceQuoteKey.public_url
        .split('/')
        .reverse()[0];

      const { data: exportData, error: exportError } =
        await exportPriceQuoteFile({
          context: {
            anonymous: true,
          },
          variables: {
            priceQuoteKey: detailsKey,
          },
        });

      if (
        exportError ||
        !exportData?.getPriceQuoteDetails.price_quote_document_url
      ) {
        throw new Error('Not able to export Price Quote CSV file');
      }

      await openWebBrowserURL(
        `${exportData.getPriceQuoteDetails.price_quote_document_url}`,
      );
    },
    [getPriceQuoteDetailsKey, exportPriceQuoteFile],
  );

  return { exportPriceQuoteCSV, loading: loading || exportLoading };
};
